import { NavLink, useLocation } from 'react-router-dom';
import {
  ChartBar, Funnel, Users, Buildings, BookOpen,
  CurrencyInr, ChatCircle, ChartLine, GearSix,
  ClipboardText, SignOut, List, Lightning, Megaphone,
} from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';

const NAV_SECTIONS = [
  {
    label: 'MAIN',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: ChartBar },
      { path: '/pipeline', label: 'Pipeline', icon: Funnel },
      { path: '/leads', label: 'Leads', icon: Users },
    ],
  },
  {
    label: 'OPERATIONS',
    items: [
      { path: '/branches', label: 'Branches', icon: Buildings },
      { path: '/courses', label: 'Courses & Batches', icon: BookOpen },
    ],
  },
  {
    label: 'FINANCE',
    items: [
      { path: '/fees', label: 'Fees & Payments', icon: CurrencyInr },
    ],
  },
  {
    label: 'ENGAGE',
    items: [
      { path: '/communication', label: 'Communication', icon: ChatCircle },
      { path: '/automations', label: 'Automations', icon: Lightning },
    ],
  },
  {
    label: 'INSIGHTS',
    items: [
      { path: '/campaigns', label: 'Campaigns', icon: Megaphone },
      // Roles & Access (Settings page) already documents "View analytics" as
      // counselor:false, faculty:false -- wasn't actually enforced until now.
      { path: '/reports', label: 'Reports', icon: ChartLine, roles: ['super_admin', 'branch_manager', 'front_desk', 'finance'] },
      // Same gap: "View audit logs" was already documented as super_admin/
      // branch_manager only.
      { path: '/audit-logs', label: 'Audit Logs', icon: ClipboardText, roles: ['super_admin', 'branch_manager'] },
    ],
  },
  {
    label: 'ADMIN',
    items: [
      // Settings owns user/role management + org profile + license -- the
      // in-app Roles & Access matrix already documents this as super_admin
      // only, but nothing actually enforced it at the nav/route level until now.
      { path: '/settings', label: 'Settings', icon: GearSix, roles: ['super_admin'] },
    ],
  },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { logout, user } = useAuth();
  const location = useLocation();

  return (
    <aside
      data-testid="sidebar"
      className={`sidebar-noir fixed left-0 top-0 h-screen z-40 flex flex-col transition-all duration-300 ${collapsed ? 'w-[64px]' : 'w-[232px]'}`}
      style={{ backgroundColor: 'var(--sb-bg)', borderRight: '1px solid var(--sb-border)' }}
    >
      {/* Logo */}
      <div className="h-[60px] flex items-center px-3 shrink-0" style={{ borderBottom: '1px solid var(--sb-border)' }}>
        <button
          onClick={onToggle}
          data-testid="sidebar-toggle"
          className="p-1.5 rounded-lg transition-colors mr-2"
          style={{ color: 'var(--sb-text)' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--sb-hover)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <List size={18} weight="bold" />
        </button>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E04B2A' }}>
              <span className="text-[11px] font-extrabold text-white font-display">A</span>
            </div>
            <span className="font-display font-bold text-[15px] tracking-tight" style={{ color: 'var(--sb-text-active)' }}>
              YuHasPro
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {NAV_SECTIONS.map((section) => {
          const visibleItems = section.items.filter(item => !item.roles || item.roles.includes(user?.role));
          if (visibleItems.length === 0) return null;
          return (
          <div key={section.label} className="mb-4">
            {!collapsed && (
              <div
                className="px-3 mb-1.5 text-[9px] font-bold tracking-[0.16em] uppercase"
                style={{ color: 'rgba(255,255,255,0.2)' }}
              >
                {section.label}
              </div>
            )}
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                  className="flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] transition-all duration-150 mb-0.5 relative"
                  style={{
                    color: isActive ? 'var(--sb-text-active)' : 'var(--sb-text)',
                    backgroundColor: isActive ? 'var(--sb-active-bg)' : 'transparent',
                    fontWeight: isActive ? 600 : 400,
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--sb-hover)'; e.currentTarget.style.color = 'var(--sb-text-active)'; }}
                  onMouseLeave={e => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--sb-text)'; } }}
                  title={collapsed ? item.label : undefined}
                >
                  {isActive && (
                    <div
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full"
                      style={{ backgroundColor: 'var(--sb-active-indicator)' }}
                    />
                  )}
                  <Icon size={17} weight={isActive ? 'fill' : 'regular'} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div className="p-3 shrink-0" style={{ borderTop: '1px solid var(--sb-border)' }}>
        {!collapsed && user && (
          <div className="mb-2 px-2">
            <p className="text-[12px] font-semibold truncate" style={{ color: 'var(--sb-text-active)' }}>{user.name}</p>
            <p className="text-[10px] truncate capitalize" style={{ color: 'var(--sb-text)' }}>{user.role?.replace('_', ' ')}</p>
          </div>
        )}
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-2 px-3 py-[7px] rounded-lg text-[13px] w-full transition-colors"
          style={{ color: 'var(--sb-text)' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#E04B2A'; e.currentTarget.style.backgroundColor = 'rgba(224,75,42,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--sb-text)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <SignOut size={17} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
